import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, ReactNode } from 'react';
import SlidingPane from 'react-sliding-pane';
import useAutoFocus from 'src/hooks/useAutoFocus';
import useRemoveScrollbar from 'src/hooks/useRemoveScrollbar';

interface SidePanelProps {
  children?: ReactNode;
  title?: string;
  isOpen: boolean;
  position?: 'left' | 'right' | 'bottom';
  width?: string;
  onClose: () => void;
  className?: string;
  overlayClassName?: string;
  titleClassName?: string;
  closeOnEsc?: boolean;
  /** Wraps children in an absolute inset-0 flex-col container so the child
   *  can implement a sticky header + scrollable body without fighting the
   *  library's overflow-y:auto / CSS transform. */
  fullHeight?: boolean;
}

const SidePanel: FC<SidePanelProps> = ({
  children,
  isOpen,
  position,
  width,
  onClose,
  title,
  className,
  overlayClassName,
  titleClassName,
  closeOnEsc = true,
  fullHeight = false,
}) => {
  useRemoveScrollbar(isOpen);
  const closeButtonRef = useAutoFocus(isOpen, [title, isOpen]);
  return (
    <SlidingPane
      className={className}
      overlayClassName={`${overlayClassName || ''} z-[100]`}
      isOpen={isOpen}
      from={position}
      width={width}
      hideHeader={true}
      onRequestClose={onClose}
      shouldCloseOnEsc={closeOnEsc}
    >
      {title && (
        <div
          className={`flex items-center justify-between sticky -top-6 z-[100] flex-shrink-0 ${titleClassName}`}
        >
          <span className="font-bold">{title}</span>
          <button
            className="k-button k-button-md k-button-flat k-button-flat-base k-rounded-md k-icon-button k-window-action k-dialog-action"
            onClick={onClose}
            ref={closeButtonRef}
          >
            {<FontAwesomeIcon icon={faXmark} />}
          </button>
        </div>
      )}
      {fullHeight ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </SlidingPane>
  );
};

export default SidePanel;
